import { useContext, useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import AuthContext from "../../../../store/AuthContext";
import {
  PaymentMethod,
  paymentMethodEnum,
} from "../../../../Enums/RegistrationEnum";
import RadioButton from "../../../../components/Atomos/Radio/RadioButton";
import LegacyButton from "../../../../components/Atomos/Buttons/LegacyButton";
import chevronRightButton from "../../../../assets/Fonts/chevron-right-button.svg";
import Input from "../../../../components/Atomos/Inputs/Input";
import TextArea from "../../../../components/Atomos/Inputs/TextArea";
import { useEventLoader } from "../../../../hooks/Events/useEventLoader";
import {
  createRegistration,
  updateRegistration,
  getRegistrationByPersonAndEvent,
  sendRegistrationEmail,
  checkPrepaidStatus,
  checkHostFirmStatus,
} from "../../../../Services/Registration";
import { getPaymentByRegistrationId } from "../../../../Services/Payment";
import { toastPromise } from "../../../../utils/toast";
import { IRegistration } from "../../../../interfaces/models/IRegistration";
import { IPayment } from "../../../../interfaces/models/IPayment";
import { IEvent } from "../../../../interfaces/models/IEvent";
import {
  generateInvoicePDFWithLogo,
  saveInvoicePDFToBlob,
} from "../../../../utils/generateInvoicePDF";
import { saveInvoiceHistory } from "../../../../Services/Invoice";
import { getFirmById } from "../../../../Services/Firm";
import Modal from "../../../../components/Atomos/Modals/Modal";
import {
  getSpecialEventsTotalAmount,
  calculateInvoiceAmount,
} from "../../../../utils/invoiceCalculations";
import { getAccountingMailingAddressForInvoice } from "../../../../utils/firmInvoiceAddress";
import GuestFormSlots from "../../../../components/GuestFormSlots/GuestFormSlots";
import {
  deriveSpecialEventGuestNames,
  normalizeSpecialEventGuestsMap,
  sanitizeSpecialEventGuestsForPersist,
} from "../../../../utils/specialEventGuests";
import {
  countEnabledGuestSlots,
  createEmptyGuestFormSlots,
  formSlotsToGuests,
  guestsToFormSlots,
  mergeSpecialEventFormSlots,
  specialEventFormSlotsToMap,
  validateAllGuestFormSlots,
  type GuestFormSlot,
} from "../../../../utils/registrationGuestFormSlots";
import {
  normalizeRegistrationGuests,
  sanitizeGuestsForPersist,
} from "../../../../utils/registrationGuests";

const getRegistrantFee = (
  event: IEvent,
  registrationDateSec?: number,
): number | null => {
  const fee = event.fee;
  if (!fee) {
    return null;
  }

  const regDateSec = registrationDateSec ?? Math.floor(Date.now() / 1000);
  const noRefundDateSec = fee.noRefundDate ?? null;
  const shouldUseLateFees =
    noRefundDateSec !== null && regDateSec >= noRefundDateSec;

  if (shouldUseLateFees && fee.lateFee !== null && fee.lateFee !== undefined) {
    return fee.lateFee;
  }

  return fee.memberFee ?? fee.fee ?? null;
};

const getGuestFee = (
  event: IEvent,
  registrationDateSec?: number,
): number | null => {
  const fee = event.fee;
  if (!fee) {
    return null;
  }

  const regDateSec = registrationDateSec ?? Math.floor(Date.now() / 1000);
  const noRefundDateSec = fee.noRefundDate ?? null;
  const shouldUseLateFees =
    noRefundDateSec !== null && regDateSec >= noRefundDateSec;

  if (
    shouldUseLateFees &&
    fee.guestLateFee !== null &&
    fee.guestLateFee !== undefined
  ) {
    return fee.guestLateFee;
  }

  return fee.guestFee ?? null;
};

const resolveGuestNamesFromSlots = (
  slots: GuestFormSlot[],
): { guestName: string; guestName2: string } => ({
  guestName:
    slots[0]?.enabled && slots[0].name.trim() ? slots[0].name.trim() : "",
  guestName2:
    slots[1]?.enabled && slots[1].name.trim() ? slots[1].name.trim() : "",
});

const getGuestFeeAmount = (
  event: IEvent,
  registrationDateSec?: number,
): number => {
  const fee = event.fee;
  if (!fee) {
    return 0;
  }

  const regDateSec = registrationDateSec ?? Math.floor(Date.now() / 1000);
  const noRefundDateSec = fee.noRefundDate ?? null;
  const shouldUseLateFees =
    noRefundDateSec !== null && regDateSec >= noRefundDateSec;

  if (
    shouldUseLateFees &&
    fee.guestLateFee !== null &&
    fee.guestLateFee !== undefined
  ) {
    return fee.guestLateFee;
  }

  if (fee.guestFee !== null && fee.guestFee !== undefined) {
    return fee.guestFee;
  }

  return 0;
};

const calculateTotalAmount = (
  event: IEvent,
  guestName: string,
  guestName2: string,
  existingPayment: IPayment | null,
  isHostFirm?: boolean,
  additionalFee?: number,
  selectedSpecialEventIds?: Set<string>,
  specialEventGuestNames?: Record<string, string | string[]>,
  registrationDateSec?: number,
  isPrepaid?: boolean,
): number => {
  const fee = event.fee;
  if (!fee) {
    return getSpecialEventsTotalAmount(event, {
      selectedSpecialEventIds: Array.from(selectedSpecialEventIds ?? []),
      specialEventGuestNames: specialEventGuestNames ?? {},
    });
  }

  const regDateSec = registrationDateSec ?? Math.floor(Date.now() / 1000);
  const noRefundDateSec = fee.noRefundDate ?? null;
  const shouldUseLateFees =
    noRefundDateSec !== null && regDateSec >= noRefundDateSec;

  const additionalFeeValue = additionalFee || 0;
  const specialEventsTotal = getSpecialEventsTotalAmount(event, {
    selectedSpecialEventIds: Array.from(selectedSpecialEventIds ?? []),
    specialEventGuestNames: specialEventGuestNames ?? {},
  });

  const numberOfGuests = normalizeRegistrationGuests({
    guestName,
    guestName2,
  }).length;
  const hasGuest = numberOfGuests > 0;
  const guestFeePerGuest = getGuestFeeAmount(event, registrationDateSec);
  const guestAmount = guestFeePerGuest * numberOfGuests;

  if (isHostFirm === true || isPrepaid === true) {
    return guestAmount + additionalFeeValue + specialEventsTotal;
  }

  if (
    existingPayment &&
    existingPayment.paymentMethod === "noCharge" &&
    (existingPayment.status === "paidSPR" ||
      existingPayment.status === "unpaidSPR") &&
    !existingPayment.guestPaid &&
    hasGuest
  ) {
    return guestAmount + additionalFeeValue + specialEventsTotal;
  }

  if (existingPayment && !existingPayment.guestPaid && hasGuest) {
    return guestAmount + additionalFeeValue + specialEventsTotal;
  }

  let amount = 0;
  if (shouldUseLateFees && fee.lateFee !== null && fee.lateFee !== undefined) {
    amount = fee.lateFee;
  } else {
    amount = fee.fee || fee.memberFee || 0;
  }

  if (hasGuest) {
    amount += guestAmount;
  }

  amount += additionalFeeValue;
  amount += specialEventsTotal;

  return amount;
};

export const DelegateRegistration = () => {
  const { loggedPerson } = useContext(AuthContext);
  const navigate = useNavigate();
  const { id: eventId } = useParams<{ id: string }>();
  const event = useEventLoader(eventId);
  const [badgeName, setBadgeName] = useState("");
  const [specialRequests, setSpecialRequests] = useState("");
  const [paymentMethod, setPaymentMethod] =
    useState<PaymentMethod>("creditCard");
  const [existingRegistration, setExistingRegistration] =
    useState<IRegistration | null>(null);
  const [existingPayment, setExistingPayment] = useState<IPayment | null>(null);
  const [guestSlots, setGuestSlots] = useState<GuestFormSlot[]>(
    createEmptyGuestFormSlots(),
  );
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isPrepaid, setIsPrepaid] = useState<boolean | null>(null);
  const [isHostFirm, setIsHostFirm] = useState<boolean | null>(null);
  const [selectedSpecialEvents, setSelectedSpecialEvents] = useState<
    Set<string>
  >(new Set());
  const [specialEventGuestSlots, setSpecialEventGuestSlots] = useState<
    Record<string, GuestFormSlot[]>
  >({});
  const [showGuestValidationModal, setShowGuestValidationModal] =
    useState(false);
  const [guestValidationMessage, setGuestValidationMessage] = useState("");

  useEffect(() => {
    const loadExistingRegistration = async () => {
      if (!eventId || !loggedPerson?.id) {
        return;
      }

      try {
        const registration = await getRegistrationByPersonAndEvent(
          loggedPerson.id,
          eventId,
        );
        if (registration) {
          setExistingRegistration(registration);
          setBadgeName(registration.badgeName || "");
          const registrationDateSec =
            registration.registrationDate ?? Math.floor(Date.now() / 1000);
          setGuestSlots(
            guestsToFormSlots(
              normalizeRegistrationGuests(registration),
              registrationDateSec,
            ),
          );
          setSpecialRequests(registration.specialRequests || "");
          if (
            registration.paymentMethod === "creditCard" ||
            registration.paymentMethod === "wireTransfer" ||
            registration.paymentMethod === "check"
          ) {
            setPaymentMethod(registration.paymentMethod);
          }
          if (
            Array.isArray(registration.selectedSpecialEventIds) &&
            registration.selectedSpecialEventIds.length > 0
          ) {
            setSelectedSpecialEvents(
              new Set(registration.selectedSpecialEventIds),
            );
          }
          const normalizedSpecialGuests =
            normalizeSpecialEventGuestsMap(registration);
          const hydratedSpecialSlots: Record<string, GuestFormSlot[]> = {};
          for (const [programId, guests] of Object.entries(
            normalizedSpecialGuests,
          )) {
            hydratedSpecialSlots[programId] = guestsToFormSlots(
              guests,
              registrationDateSec,
            );
          }
          setSpecialEventGuestSlots(hydratedSpecialSlots);

          if (registration.isHostFirm !== undefined) {
            setIsHostFirm(registration.isHostFirm);
          }
          if (registration.prepaid !== undefined) {
            setIsPrepaid(registration.prepaid);
          }

          if (registration.id && event?.id) {
            try {
              const payment = await getPaymentByRegistrationId(registration.id);
              if (payment && event) {
                const isValidPayment =
                  payment.eventId === event.id &&
                  payment.person?.some(
                    (p: any) => p.personId === loggedPerson.id,
                  );

                if (isValidPayment) {
                  setExistingPayment(payment);
                } else {
                  setExistingPayment(null);
                }
              } else {
                setExistingPayment(null);
              }
            } catch (error) {
              setExistingPayment(null);
            }
          }
        } else {
          setExistingRegistration(null);
          setExistingPayment(null);
        }
        if (event?.id && loggedPerson?.id) {
          // Only check status if registration doesn't exist or doesn't have these fields
          // Otherwise, use the values from the registration (server is source of truth)
          if (!registration || registration.prepaid === undefined) {
            try {
              const prepaidStatus = await checkPrepaidStatus(
                event.id,
                loggedPerson.id,
                registration?.id,
              );
              setIsPrepaid(prepaidStatus);
            } catch (error) {
              setIsPrepaid(null);
            }
          }
          if (!registration || registration.isHostFirm === undefined) {
            try {
              const hostFirmStatus = await checkHostFirmStatus(
                event.id,
                loggedPerson.id,
                registration?.id,
              );
              setIsHostFirm(hostFirmStatus.isHostFirm);
            } catch (error) {
              setIsHostFirm(null);
            }
          }
        }
      } catch (error) {
        setExistingRegistration(null);
        setExistingPayment(null);
        setIsPrepaid(null);
        setIsHostFirm(null);
      }
    };

    if (event?.id && loggedPerson?.id) {
      loadExistingRegistration();
    }
  }, [event?.id, loggedPerson?.id, eventId]);

  const paymentMethods = useMemo(() => {
    const allowedMethods: (keyof typeof paymentMethodEnum)[] = [
      "creditCard",
      "wireTransfer",
      "check",
    ];

    return Object.entries(paymentMethodEnum)
      .filter(([key]) =>
        allowedMethods.includes(key as keyof typeof paymentMethodEnum),
      )
      .map(([key, value]) => ({
        id: key as PaymentMethod,
        name: value,
      }));
  }, []);

  const specialEventPrograms = useMemo(() => {
    if (!event?.program) {
      return [];
    }
    return event.program
      .map((program, index) => ({
        ...program,
        originalIndex: index,
        programId: (program as { id?: string }).id,
      }))
      .filter((p) => p.requiredRegistration && p.programId);
  }, [event?.program]);

  const paymentDescription = {
    creditCard: "I will pay with a credit card",
    wireTransfer:
      "I will pay by wire transfer upon receipt of payment instruction",
    check: "I will pay by check upon receipt of payment instruction",
  };

  const specialEventProgramIds = useMemo(
    () =>
      specialEventPrograms
        .map((program) => program.programId as string)
        .filter(Boolean),
    [specialEventPrograms],
  );

  const registrationDateSec =
    existingRegistration?.registrationDate ?? Math.floor(Date.now() / 1000);

  useEffect(() => {
    setSpecialEventGuestSlots((prev) =>
      mergeSpecialEventFormSlots(
        prev,
        specialEventProgramIds,
        registrationDateSec,
      ),
    );
  }, [specialEventProgramIds, registrationDateSec]);

  const specialEventGuestNames = useMemo(() => {
    const guestsMap = specialEventFormSlotsToMap(
      specialEventGuestSlots,
      Array.from(selectedSpecialEvents),
      registrationDateSec,
    );
    return deriveSpecialEventGuestNames(guestsMap) ?? {};
  }, [specialEventGuestSlots, selectedSpecialEvents, registrationDateSec]);

  const programTitles = useMemo(() => {
    const titles: Record<string, string> = {};
    for (const program of specialEventPrograms) {
      const programId = program.programId as string;
      titles[programId] =
        program.registrationPageSessionTitle ||
        `${event?.title ?? ""} - ${program.title}`;
    }
    return titles;
  }, [event?.title, specialEventPrograms]);

  const hasSponsorExtraCharges = useMemo(() => {
    const hasGuest = countEnabledGuestSlots(guestSlots) > 0;
    const hasSpecialEvents = selectedSpecialEvents.size > 0;
    return hasGuest || hasSpecialEvents;
  }, [guestSlots, selectedSpecialEvents]);

  const lockPaymentMethodNoCharge =
    isHostFirm === true && !hasSponsorExtraCharges;

  const shouldHidePaymentMethodSelection = useMemo(() => {
    const prepaidNoExtras = isPrepaid === true && !hasSponsorExtraCharges;
    const sponsorNoExtras = isHostFirm === true && !hasSponsorExtraCharges;
    return prepaidNoExtras || sponsorNoExtras;
  }, [isPrepaid, isHostFirm, hasSponsorExtraCharges]);

  const sponsorPaymentMessage = useMemo((): string | null => {
    if (isHostFirm !== true) {
      return null;
    }
    const hasGuest = countEnabledGuestSlots(guestSlots) > 0;
    const hasSpecialEvents = selectedSpecialEvents.size > 0;

    if (!hasGuest && !hasSpecialEvents) {
      return "Sponsor Firm — Registrant Fee is No Charge";
    }
    if (hasGuest && hasSpecialEvents) {
      return "Sponsor Firm - Please Select the Payment Method For Your Guest(s) and Special Event";
    }
    if (hasSpecialEvents) {
      return "Sponsor Firm - Please Select the Payment Method For Your Special Event";
    }
    if (hasGuest) {
      return "Sponsor Firm - Please Select the Payment Method For Your Guest(s)";
    }
    return null;
  }, [isHostFirm, guestSlots, selectedSpecialEvents]);

  useEffect(() => {
    if (lockPaymentMethodNoCharge) {
      setPaymentMethod("noCharge");
    } else if (isHostFirm === true && paymentMethod === "noCharge") {
      setPaymentMethod("creditCard");
    }
  }, [lockPaymentMethodNoCharge, isHostFirm, paymentMethod]);

  const shouldShowPaymentSection = useMemo(() => {
    if (!event) {
      return false;
    }

    if (isHostFirm === true) {
      return true;
    }

    if (isPrepaid === true) {
      return true;
    }

    const hasSpecialEventsToPay =
      specialEventPrograms.length > 0 && selectedSpecialEvents.size > 0;
    if (hasSpecialEventsToPay) {
      return true;
    }

    const registrantFee = getRegistrantFee(
      event,
      existingRegistration?.registrationDate,
    );
    if (registrantFee === null) {
      return false;
    }

    if (!existingPayment) {
      return true;
    }

    const guestFee = getGuestFee(event, existingRegistration?.registrationDate);
    if (existingPayment && !existingPayment.guestPaid && guestFee != null) {
      return true;
    }

    return false;
  }, [
    existingPayment,
    event,
    existingRegistration?.registrationDate,
    specialEventPrograms.length,
    selectedSpecialEvents.size,
    isHostFirm,
    isPrepaid,
  ]);

  const additionalFeeValue = useMemo(() => {
    if (existingRegistration?.additionalFee == null) {
      return 0;
    }
    return typeof existingRegistration.additionalFee === "number"
      ? existingRegistration.additionalFee
      : parseFloat(String(existingRegistration.additionalFee)) || 0;
  }, [existingRegistration?.additionalFee]);

  const totalEventFee = useMemo(() => {
    if (!event) {
      return null;
    }

    if (isHostFirm === true && !hasSponsorExtraCharges) {
      return 0;
    }

    const { guestName, guestName2 } = resolveGuestNamesFromSlots(guestSlots);
    return calculateTotalAmount(
      event,
      guestName,
      guestName2,
      existingPayment,
      isHostFirm === true,
      additionalFeeValue,
      selectedSpecialEvents,
      specialEventGuestNames,
      registrationDateSec,
      isPrepaid === true,
    );
  }, [
    event,
    guestSlots,
    existingPayment,
    isHostFirm,
    isPrepaid,
    hasSponsorExtraCharges,
    additionalFeeValue,
    selectedSpecialEvents,
    specialEventGuestNames,
    registrationDateSec,
  ]);

  const totalEventFeeLabel = useMemo(() => {
    if (totalEventFee == null || totalEventFee <= 0) {
      return null;
    }
    if (isPrepaid === true && !hasSponsorExtraCharges) {
      return null;
    }
    const currency = event?.fee?.currency || "USD";
    const formattedAmount = totalEventFee.toLocaleString("en-US", {
      minimumFractionDigits: 0,
      maximumFractionDigits: 2,
    });
    const labelText =
      isPrepaid === true && hasSponsorExtraCharges
        ? "Additional Payment Total:"
        : "Total Event Fee:";
    return (
      <p className="text-base md:text-lg text-gray-font mb-4">
        <span className="font-semibold">{labelText}</span>{" "}
        ${formattedAmount} {currency}{" "}
        <span className="text-gray-500">
          (All fees will be processed in USD)
        </span>
      </p>
    );
  }, [event?.fee?.currency, hasSponsorExtraCharges, isPrepaid, totalEventFee]);

  const hasEventFee = (event: IEvent): boolean => {
    const fee = event?.fee;
    if (!fee) {
      return false;
    }

    const hasValue = (val: any) => val !== null && val !== undefined && val > 0;
    return (
      hasValue(fee.fee) ||
      hasValue(fee.memberFee) ||
      hasValue(fee.guestFee) ||
      hasValue(fee.guestLateFee) ||
      hasValue(fee.lateFee) ||
      hasValue(fee.specialEventFee)
    );
  };

  const handleSendRegistrationInvoice = useCallback(
    async (
      registrationId: string,
      eventId: string,
      registration: IRegistration,
    ) => {
      if (!event || !loggedPerson || !registrationId) {
        return;
      }

      if (!hasEventFee(event)) {
        try {
          await sendRegistrationEmail(registrationId, eventId);
        } catch (emailError) {}
        return;
      }

      try {
        const feeForInvoice = event.fee;
        if (!feeForInvoice) {
          return;
        }

        const additionalFeeValueForInvoice = parseFloat(
          (registration as any).additionalFee || "0",
        );
        const isPrepaid = (registration as any).prepaid === true;
        const isSponsorPaid = registration.paymentStatus === "paidSPR";

        const regDateSecForInvoice =
          registration.registrationDate ?? Math.floor(Date.now() / 1000);
        const noRefundDateSecForInvoice = feeForInvoice.noRefundDate ?? null;
        const shouldUseLateFeesForInvoice =
          noRefundDateSecForInvoice !== null &&
          regDateSecForInvoice >= noRefundDateSecForInvoice;

        const memberFeeForInvoice =
          isPrepaid || isSponsorPaid
            ? 0
            : shouldUseLateFeesForInvoice
              ? (feeForInvoice.lateFee ??
                feeForInvoice.memberFee ??
                feeForInvoice.fee ??
                0)
              : (feeForInvoice.memberFee ?? feeForInvoice.fee ?? 0);
        const totalInvoiceAmount = calculateInvoiceAmount(
          event,
          registration,
          additionalFeeValueForInvoice,
        );
        const currency = feeForInvoice.currency || "USD";

        let invoiceAddressData:
          | {
              address1?: string;
              address2?: string;
              city?: string;
              state?: string;
              zipCode?: string;
              country?: string;
            }
          | undefined = undefined;

        let firmName: string | undefined = undefined;
        const firmId = registration.firmId || loggedPerson.firm;
        if (firmId) {
          try {
            const firmData = await getFirmById(firmId);
            if (firmData) {
              firmName = firmData.name || firmData.firmName || undefined;
              invoiceAddressData =
                getAccountingMailingAddressForInvoice(firmData);
            }
          } catch (firmError) {}
        }

        const eventName = (event.title || "Event")
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .replace(/\s+/g, "_");
        const memberName = (
          [loggedPerson.name, loggedPerson.lastName]
            .filter(Boolean)
            .join(" ") ||
          registration.badgeName ||
          "Member"
        )
          .replace(/[^a-zA-Z0-9\s-]/g, "")
          .replace(/\s+/g, "_");
        const currentTimestamp = Math.floor(Date.now() / 1000);
        const date = new Date(currentTimestamp * 1000);
        const month = (date.getUTCMonth() + 1).toString().padStart(2, "0");
        const day = date.getUTCDate().toString().padStart(2, "0");
        const year = date.getUTCFullYear();
        const dateStr = `${month}-${day}-${year}`;
        const invoicePrefix =
          isPrepaid || isSponsorPaid ? "Paid_Invoice" : "Due_Invoice";
        const filename = `${invoicePrefix}_${eventName}_${memberName}_${dateStr}.pdf`;

        const paymentMethodOptions = Object.entries(paymentMethodEnum).map(
          ([key, value]) => ({
            id: key,
            name: value,
          }),
        );

        let amountAlreadyPaidForInvoice = 0;
        try {
          const existingPaymentForInvoice =
            await getPaymentByRegistrationId(registrationId);
          if (
            existingPaymentForInvoice &&
            existingPaymentForInvoice.eventId === event.id
          ) {
            amountAlreadyPaidForInvoice = existingPaymentForInvoice.amount ?? 0;
          }
        } catch {
          // use 0 if we can't fetch payments
        }

        let pdfBlob: Blob | null = null;
        let savedPdfPath: string | null = null;

        try {
          let feeToUse = event.fee;
          if (
            ((registration as any).prepaid === true || isSponsorPaid) &&
            event.fee
          ) {
            feeToUse = {
              ...event.fee,
              fee: 0,
              memberFee: 0,
              lateFee: 0,
              specialEventFee: event.fee.specialEventFee ? 0 : undefined,
            };
          }

          const programForInvoice = event.program?.map((p: any) => ({
            id: p.id,
            title: p.title,
            registrationPageSessionTitle: p.registrationPageSessionTitle,
            fee: p.fee,
            specialEventGuestFee: p.specialEventGuestFee,
          }));

          const eventPaymentForPdf = isPrepaid
            ? memberFeeForInvoice.toString()
            : isSponsorPaid
              ? totalInvoiceAmount.toString()
              : String(amountAlreadyPaidForInvoice);

          pdfBlob = await generateInvoicePDFWithLogo({
            event: {
              id: event.id,
              title: event.title,
              startDate: event.startDate,
              location: event.location,
              fee: feeToUse,
              program: programForInvoice,
            },
            person: {
              name:
                [loggedPerson.name, loggedPerson.lastName]
                  .filter(Boolean)
                  .join(" ") ||
                registration.badgeName ||
                "",
              email: loggedPerson.firmEmail || "",
            },
            firm: firmName ? { name: firmName } : null,
            badgeName: registration.badgeName,
            guests: normalizeRegistrationGuests(registration),
            guestName: registration.guestName,
            guestName2: registration.guestName2,
            eventPayment: eventPaymentForPdf,
            creditPayment: "0",
            amountRefunded: "0",
            additionalFee: additionalFeeValueForInvoice.toString(),
            paymentMethod: isPrepaid
              ? "prepaid"
              : isSponsorPaid
                ? "noCharge"
                : registration.paymentMethod || "creditCard",
            paymentMethodOptions,
            paid: isPrepaid || isSponsorPaid ? "paid" : "unpaid",
            notes: (registration as any).note || "",
            eventId,
            registrationId: registration.id,
            personId: loggedPerson.id,
            filename,
            invoiceAddress: invoiceAddressData,
            selectedSpecialEventIds: registration.selectedSpecialEventIds,
            specialEventGuestNames: registration.specialEventGuestNames,
            registrationDate: registration.registrationDate,
            isPrepaid,
          });

          if (pdfBlob) {
            const storagePathOrUrl = await saveInvoicePDFToBlob(
              pdfBlob,
              filename,
              loggedPerson.id || "",
              eventId,
            );

            if (storagePathOrUrl) {
              if (storagePathOrUrl.startsWith("http")) {
                savedPdfPath = storagePathOrUrl;
              } else {
                const storageAccount =
                  process.env.REACT_APP_STORAGE_ACCOUNT_NAME ||
                  process.env.REACT_APP_STORAGE_ACCOUNT ||
                  "wsgfilestorageqa";
                const container =
                  process.env.REACT_APP_STORAGE_ACCOUNT_CONTAINER_NAME ||
                  process.env.REACT_APP_STORAGE_CONTAINER ||
                  "testing";
                const cleanStoragePath = storagePathOrUrl.startsWith("/")
                  ? storagePathOrUrl.substring(1)
                  : storagePathOrUrl;
                savedPdfPath = `https://${storageAccount}.blob.core.windows.net/${container}/${cleanStoragePath}`;
              }
            }
          }
        } catch (pdfError) {}

        const ccEmail = eventId
          ? sessionStorage.getItem(`ccEmail_${eventId}`)
          : null;

        try {
          await sendRegistrationEmail(
            registrationId,
            eventId,
            savedPdfPath || undefined,
            isPrepaid || isSponsorPaid,
          );
        } catch (emailError) {}

        try {
          const oneHourInMs = 60 * 60 * 1000;
          const registrationTimestamp = registration.registrationDate
            ? registration.registrationDate * 1000
            : Date.now();
          const isOlderThanOneHour =
            Date.now() - registrationTimestamp > oneHourInMs;
          const sentByValue = isOlderThanOneHour ? "System" : "orig-reg";

          await saveInvoiceHistory({
            registrationId: registration.id || "",
            eventId: eventId,
            currency: currency,
            amount: totalInvoiceAmount,
            paymentStatus: isPrepaid || isSponsorPaid ? "paid" : "pending",
            pdfPath: savedPdfPath || undefined,
            filename: filename,
            ccEmail: ccEmail?.trim() || undefined,
            sentBy: sentByValue,
          });
        } catch (historyError) {}
      } catch (error) {}
    },
    [event, loggedPerson],
  );

  if (!event) {
    return <div></div>;
  }

  const registrantName = `${loggedPerson?.name || ""} ${
    loggedPerson?.lastName || ""
  }`.trim();

  const handleSubmit = async () => {
    if (
      !event ||
      !event.id ||
      !loggedPerson?.id ||
      !registrantName ||
      isSubmitting
    ) {
      return;
    }

    const validationError = validateAllGuestFormSlots(
      guestSlots,
      specialEventGuestSlots,
      selectedSpecialEvents,
      programTitles,
    );
    if (validationError) {
      setGuestValidationMessage(validationError);
      setShowGuestValidationModal(true);
      return;
    }

    setIsSubmitting(true);

    try {
      const contentDiv = document.getElementById("content-container");
      if (contentDiv?.scrollTop) {
        sessionStorage.setItem(
          "eventScrollPosition",
          contentDiv.scrollTop.toString(),
        );
      }

      const { guestName, guestName2 } = resolveGuestNamesFromSlots(guestSlots);
      const hasGuest = countEnabledGuestSlots(guestSlots) > 0;
      const hasSpecialEventsSelected = selectedSpecialEvents.size > 0;

      const submitRegistrationDateSec =
        existingRegistration?.registrationDate ?? Math.floor(Date.now() / 1000);
      const additionalFeeForTotal =
        existingRegistration?.additionalFee != null
          ? typeof existingRegistration.additionalFee === "number"
            ? existingRegistration.additionalFee
            : parseFloat(String(existingRegistration.additionalFee)) || 0
          : 0;
      // The prepaid check runs asynchronously on mount; if it hasn't resolved yet
      // (isPrepaid === null) re-check now so we never submit a stale "not prepaid"
      // default that the server would later flip to prepaid, leaving the registration
      // inconsistent (prepaid true + creditCard/pending).
      let resolvedIsPrepaid = isPrepaid;
      if (resolvedIsPrepaid === null && event.id && loggedPerson.id) {
        try {
          resolvedIsPrepaid = await checkPrepaidStatus(
            event.id,
            loggedPerson.id,
            existingRegistration?.id,
          );
          setIsPrepaid(resolvedIsPrepaid);
        } catch {
          resolvedIsPrepaid = false;
        }
      }

      const submitTotalAmount = calculateTotalAmount(
        event,
        guestName,
        guestName2,
        existingPayment,
        isHostFirm === true,
        additionalFeeForTotal,
        selectedSpecialEvents,
        specialEventGuestNames,
        submitRegistrationDateSec,
        resolvedIsPrepaid === true,
      );
      const useNoChargeForHostFirm =
        isHostFirm === true && !hasSponsorExtraCharges;

      // Zero display/submit total only means noCharge when not prepaid.
      const useNoChargeForZeroTotal =
        submitTotalAmount === 0 && resolvedIsPrepaid !== true;

      const effectivePaymentMethod =
        useNoChargeForHostFirm || useNoChargeForZeroTotal
          ? "noCharge"
          : resolvedIsPrepaid
            ? hasGuest || hasSpecialEventsSelected
              ? paymentMethod
              : "prepaid"
            : paymentMethod;

      const guestsForSave = sanitizeGuestsForPersist(
        formSlotsToGuests(guestSlots, {
          fallbackRegistrationDate: submitRegistrationDateSec,
        }),
        submitRegistrationDateSec,
      );
      const specialEventGuestsForSave = sanitizeSpecialEventGuestsForPersist(
        specialEventFormSlotsToMap(
          specialEventGuestSlots,
          Array.from(selectedSpecialEvents),
          submitRegistrationDateSec,
        ),
        Array.from(selectedSpecialEvents),
        submitRegistrationDateSec,
      );
      const specialEventGuestNamesForSave = deriveSpecialEventGuestNames(
        specialEventGuestsForSave,
      );

      const registrationData = {
        eventId: event.id,
        personId: loggedPerson.id,
        attendeeType: "regular" as const,
        firmId: loggedPerson.firm || "",
        mainOffice: loggedPerson.mainOffice || undefined,
        mainOfficeName: loggedPerson.mainOfficeName || undefined,
        additionalOffices: loggedPerson.additionalOffices?.length
          ? loggedPerson.additionalOffices
          : undefined,
        badgeName,
        guests: guestsForSave,
        guestName: undefined,
        guestName2: undefined,
        specialRequests: specialRequests || undefined,
        paymentMethod: effectivePaymentMethod,
        prepaid: resolvedIsPrepaid === true,
        registrationDate:
          existingRegistration?.registrationDate ||
          Math.floor(Date.now() / 1000),
        selectedSpecialEventIds: Array.from(selectedSpecialEvents),
        specialEventGuests:
          Object.keys(specialEventGuestsForSave).length > 0
            ? specialEventGuestsForSave
            : undefined,
        specialEventGuestNames: specialEventGuestNamesForSave,
      };

      const effectivePaymentStatus =
        useNoChargeForHostFirm || useNoChargeForZeroTotal
          ? "completed"
          : isHostFirm === true && hasSponsorExtraCharges
            ? "paidSPR"
            : resolvedIsPrepaid
              ? hasGuest || hasSpecialEventsSelected
                ? "partial"
                : "completed"
              : "pending";

      const promise = existingRegistration?.id
        ? updateRegistration({
            ...existingRegistration,
            ...registrationData,
            id: existingRegistration.id,
            paymentStatus: effectivePaymentStatus,
          })
        : createRegistration({
            ...registrationData,
            paymentStatus: effectivePaymentStatus,
            createdFrom: "member",
          });

      const registrationResponse = await toastPromise(promise, {
        pending: "Saving registration...",
        success: "Registration saved successfully!",
        error: (error: any) =>
          error?.response?.data?.error ||
          error?.response?.data?.message ||
          error?.message ||
          "Failed to save registration. Please try again.",
      });

      const finalRegistrationId =
        existingRegistration?.id ||
        registrationResponse?.id ||
        (await getRegistrationByPersonAndEvent(loggedPerson.id, event.id))?.id;

      const finalRegistration =
        registrationResponse ||
        (finalRegistrationId
          ? await getRegistrationByPersonAndEvent(loggedPerson.id, event.id)
          : null);

      if (finalRegistration?.isHostFirm !== undefined) {
        setIsHostFirm(finalRegistration.isHostFirm);
      }
      if (finalRegistration?.prepaid !== undefined) {
        setIsPrepaid(finalRegistration.prepaid);
      } else if (effectivePaymentMethod === "prepaid") {
        // Backend may not always return prepaid in response; keep UI in sync when we sent prepaid
        setIsPrepaid(true);
      }

      if (finalRegistrationId && event.id && finalRegistration) {
        try {
          await handleSendRegistrationInvoice(
            finalRegistrationId,
            event.id,
            finalRegistration,
          );
        } catch (error) {
          try {
            await sendRegistrationEmail(finalRegistrationId, event.id);
          } catch {}
        }
      } else if (finalRegistrationId && event.id) {
        try {
          await sendRegistrationEmail(finalRegistrationId, event.id);
        } catch {}
      }

      const actualIsHostFirm =
        finalRegistration?.isHostFirm === true ||
        (finalRegistration?.isHostFirm === undefined && isHostFirm === true);

      const guestFeeForRedirect = getGuestFee(
        event,
        finalRegistration?.registrationDate,
      );
      const hasGuestForRedirect = countEnabledGuestSlots(guestSlots) > 0;
      const specialEventsTotalForRedirect = getSpecialEventsTotalAmount(
        event,
        finalRegistration ?? {
          selectedSpecialEventIds: Array.from(selectedSpecialEvents),
          specialEventGuestNames,
          specialEventGuests: specialEventFormSlotsToMap(
            specialEventGuestSlots,
            Array.from(selectedSpecialEvents),
            finalRegistration?.registrationDate ?? submitRegistrationDateSec,
          ),
        },
      );
      const sponsorFullySettled =
        actualIsHostFirm &&
        (!hasGuestForRedirect || (guestFeeForRedirect ?? 0) === 0) &&
        specialEventsTotalForRedirect === 0;

      if (finalRegistration?.prepaid === true) {
        const guestFeeAmount = guestFeeForRedirect ?? 0;
        if (
          (!hasGuestForRedirect || guestFeeAmount === 0) &&
          specialEventsTotalForRedirect === 0
        ) {
          navigate(-1);
          return;
        }
      }

      if (sponsorFullySettled) {
        navigate(-1);
        return;
      }

      const paymentToCheck =
        existingPayment ||
        (finalRegistrationId
          ? await getPaymentByRegistrationId(finalRegistrationId).catch(
              () => null,
            )
          : null);

      const isValidPayment =
        paymentToCheck &&
        paymentToCheck.eventId === event.id &&
        paymentToCheck.person?.some((p) => p.personId === loggedPerson.id);

      const registrationAdditionalFee =
        finalRegistration?.additionalFee != null
          ? typeof finalRegistration.additionalFee === "number"
            ? finalRegistration.additionalFee
            : parseFloat(String(finalRegistration.additionalFee)) || 0
          : 0;

      const redirectGuestNames = resolveGuestNamesFromSlots(guestSlots);
      const totalAmount = calculateTotalAmount(
        event,
        redirectGuestNames.guestName,
        redirectGuestNames.guestName2,
        isValidPayment ? paymentToCheck : null,
        actualIsHostFirm,
        registrationAdditionalFee,
        selectedSpecialEvents,
        specialEventGuestNames,
        finalRegistration?.registrationDate,
        finalRegistration?.prepaid === true,
      );

      const sponsorPaymentCoversMemberOnly =
        actualIsHostFirm &&
        isValidPayment &&
        paymentToCheck &&
        paymentToCheck.paymentMethod === "noCharge" &&
        (paymentToCheck.status === "paidSPR" ||
          paymentToCheck.status === "unpaidSPR") &&
        paymentToCheck.guestPaid !== true;

      const amountAlreadyPaid =
        isValidPayment && paymentToCheck && !sponsorPaymentCoversMemberOnly
          ? (paymentToCheck.amount ?? 0)
          : 0;
      const hasMoreToPay = totalAmount > amountAlreadyPaid;

      if (
        totalAmount === 0 ||
        !hasMoreToPay ||
        effectivePaymentMethod !== "creditCard"
      ) {
        navigate(-1);
        return;
      }

      navigate(`/events/${event.id}/registration/payment`);
    } catch {
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="">
      <Modal
        isModalOpen={showGuestValidationModal}
        toggleModal={() => setShowGuestValidationModal(false)}
        title="Guest Name Required"
        titleColor="text-primary"
        compact
        compactBodyScrolls={false}
        outerPaddingOnly
        className="max-w-lg w-full p-6 md:p-8"
        bodyClassName="!pt-4 !px-0 !pb-0"
      >
        <div className="flex flex-col gap-6">
          <p className="text-left text-sm md:text-base lg:text-lg font-normal text-gray-font leading-[18px] md:leading-6 font-sans">
            {guestValidationMessage}
          </p>
          <div className="flex justify-end border-t border-primary pt-4">
            <LegacyButton
              text="OK"
              color="primary"
              style="filled"
              content="text"
              size="medium"
              className="!w-full md:!w-[205px]"
              onClick={() => setShowGuestValidationModal(false)}
              type="button"
            />
          </div>
        </div>
      </Modal>
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-4 md:gap-4">
          <h1 className="text-[24px] md:text-[30px] lg:text-[32px] font-medium text-primary leading-8 font-decimal text-left">
            Delegate Registration
          </h1>
          <span className="text-left leading-[18px] text-sm md:text-base lg:text-lg font-normal text-gray-font">
            To register for the {event.title} please fill and submit the form
            below. You will receive confirmation of registration and payment by
            email. If you do not receive confirmation of registration within one
            day, please contact WSG at
            <a
              className="text-Default hover:text-accent-Default-dark"
              href="mailto:events@worldservicesgroup.com"
            >
              {" "}
              events@worldservicesgroup.com{" "}
            </a>
            or via phone at +1 713 650 0333.
          </span>
        </div>
        <div className="flex ">
          <div className="flex gap-4">
            <div className="w-4 h-4 bg-primary rounded-full"></div>
            <div className="w-4 h-4 bg-primary rounded-full"></div>
            <div className="w-4 h-4 bg-primary rounded-full"></div>
          </div>
        </div>

        <div
          className={`flex flex-col items-start h-auto pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left w-full overflow-hidden`}
        >
          <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
            <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
              Registrant
            </h3>
          </div>
          <div className="block text-left px-4 md:px-8 flex-1 w-full overflow-hidden">
            <div className="flex flex-col gap-5 w-full">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-start">
                <div className="flex flex-col gap-2 md:gap-[.625rem] text-base md:text-lg font-normal text-gray-font">
                  <span className="font-semibold">Registrant:</span>
                  <span>{registrantName}</span>
                </div>
                <div className="min-w-0">
                  <Input
                    title="Badge Name"
                    name="badgeName"
                    value={badgeName}
                    onChange={(e) => setBadgeName(e.target.value)}
                    placeholder=""
                    error={false}
                    required={false}
                    className="!bg-white"
                  />
                </div>
                <div className="flex flex-col gap-2 md:gap-[.625rem] text-base md:text-lg font-normal text-gray-font">
                  <span className="font-semibold">Registrant Fee:</span>
                  <span>
                    {(() => {
                      if (isHostFirm === true) {
                        return (
                          <>
                            No charge{" "}
                            <span className="text-gray-500">
                              (Sponsor firm registrant fee)
                            </span>
                          </>
                        );
                      }
                      const registrantFee = getRegistrantFee(
                        event,
                        existingRegistration?.registrationDate,
                      );
                      return registrantFee != null ? (
                        <>
                          ${registrantFee.toString()} {event.fee?.currency}{" "}
                          <span className="text-gray-500">
                            (All fees will be processed in USD)
                          </span>
                        </>
                      ) : (
                        "No cost"
                      );
                    })()}
                  </span>
                </div>
              </div>

              <div className="w-full">
                <TextArea
                  title="Special Requests"
                  name="specialRequests"
                  value={specialRequests}
                  onChange={(e) => setSpecialRequests(e.target.value)}
                  placeholder=""
                  error={false}
                  required={false}
                  maxChars={250}
                  characterCount={true}
                  height="h-[3.5rem]"
                  className="bg-white"
                />
              </div>

              {(() => {
                const guestFee = getGuestFee(
                  event,
                  existingRegistration?.registrationDate,
                );
                if (guestFee == null) {
                  return null;
                }

                return (
                  <GuestFormSlots
                    slots={guestSlots}
                    onSlotsChange={setGuestSlots}
                    variant="member"
                    className="w-full"
                    guestFeeLabel={
                      <span className="text-base md:text-lg text-gray-font">
                        Guest Fee ${guestFee.toString()} {event.fee?.currency}
                      </span>
                    }
                  />
                );
              })()}
              {(() => {
                const additionalFeeValue =
                  existingRegistration?.additionalFee != null
                    ? typeof existingRegistration.additionalFee === "number"
                      ? existingRegistration.additionalFee
                      : parseFloat(
                          String(existingRegistration.additionalFee),
                        ) || 0
                    : 0;
                return (
                  additionalFeeValue > 0 && (
                    <div className="w-full">
                      <p className="text-sm md:text-base lg:text-lg font-medium text-gray-font mb-6">
                        Additional Fee
                      </p>
                      <p className="text-base md:text-lg lg:text-xl font-normal text-gray-font">
                        ${additionalFeeValue.toFixed(2)} {event.fee?.currency}
                      </p>
                    </div>
                  )
                );
              })()}
            </div>
          </div>
        </div>

        {specialEventPrograms.length > 0 && (
          <div className="flex flex-col w-full bg-gray-15">
            <div className="border-b border-primary pb-0 flex-shrink-0 mb-4 mt-2 w-fit pl-4 md:pl-8 pt-4">
              <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans">
                Special Event(s)
              </h3>
            </div>
            <div className="flex flex-col">
              {specialEventPrograms.map((program) => {
                const startDate = new Date(program.startTime * 1000);
                const endDate = new Date(program.endTime * 1000);
                const dateStr = `${(startDate.getUTCMonth() + 1).toString().padStart(2, "0")}/${startDate.getUTCDate().toString().padStart(2, "0")}/${startDate.getUTCFullYear()}`;
                const startTimeStr = `${startDate.getUTCHours().toString().padStart(2, "0")}:${startDate.getUTCMinutes().toString().padStart(2, "0")}`;
                const endTimeStr = `${endDate.getUTCHours().toString().padStart(2, "0")}:${endDate.getUTCMinutes().toString().padStart(2, "0")}`;
                const programFee = program.fee ?? 0;
                const specialEventGuestFee = program.specialEventGuestFee ?? 0;
                const currency = event.fee?.currency || "USD";
                const programId = program.id as string;
                const isEventSelected = selectedSpecialEvents.has(programId);
                const programGuestSlots =
                  specialEventGuestSlots[programId] ??
                  createEmptyGuestFormSlots(registrationDateSec);

                return (
                  <div key={programId} className="px-4 md:px-8 pb-4">
                    <div className="flex flex-col gap-4 lg:py-4 py-2">
                      <div className="flex items-start gap-2 flex-1">
                        <input
                          type="checkbox"
                          checked={isEventSelected}
                          onChange={() => {
                            setSelectedSpecialEvents((prev) => {
                              const next = new Set(prev);
                              if (next.has(programId)) {
                                next.delete(programId);
                              }
                              else {
                                next.add(programId);
                              }
                              return next;
                            });
                          }}
                          className="mt-1 w-4 h-4 accent-primary cursor-pointer flex-shrink-0"
                        />
                        <div className="flex flex-col gap-2 flex-1">
                          <span className="text-base md:text-lg font-semibold text-gray-font leading-tight">
                            {program.registrationPageSessionTitle ||
                              `${event.title} - ${program.title}`}
                          </span>
                          <div className="flex flex-col gap-4 md:gap-6 items-start lg:items-center lg:flex-row">
                            <span className="text-base md:text-lg text-gray-font">
                              <div
                                style={{
                                  marginRight: 32,
                                  marginLeft: 4,
                                  minWidth:
                                    typeof window !== "undefined" &&
                                    window.innerWidth >= 768
                                      ? 260
                                      : undefined,
                                }}
                              >
                                Date: {dateStr}
                                <span style={{ margin: "0 24px" }}>-</span>
                                Time {startTimeStr} - {endTimeStr} hrs
                              </div>
                            </span>
                            <span className="text-base md:text-lg text-gray-font">
                              Fee: $ {programFee.toFixed(2)} {currency}
                            </span>
                            {program.specialEventGuestFee != null && (
                              <span className="text-base md:text-lg text-gray-font">
                                Guest Fee: $ {specialEventGuestFee.toFixed(2)}{" "}
                                {currency}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>

                      {isEventSelected && specialEventGuestFee > 0 && (
                        <div className="ml-7 md:ml-6">
                          <GuestFormSlots
                            slots={programGuestSlots}
                            onSlotsChange={(nextSlots) => {
                              setSpecialEventGuestSlots((prev) => ({
                                ...prev,
                                [programId]: nextSlots,
                              }));
                            }}
                            variant="member"
                          />
                        </div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {shouldShowPaymentSection && (
          <div
            className={`flex flex-col items-start h-auto pt-2 md:pt-4 pb-3 md:pb-6 px-0 bg-gray-15 transition-all text-left w-full overflow-hidden`}
          >
            {isPrepaid && (
              <div
                className={`w-full${shouldHidePaymentMethodSelection ? "" : " mb-8"}`}
              >
                <div className="border-b border-primary pb-0 flex-shrink-0 mb-4 inline-block pl-4 md:pl-8">
                  <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans">
                    Payment
                  </h3>
                </div>
                <div className="block text-left px-4 md:px-8 flex-1 w-full overflow-hidden">
                  {totalEventFeeLabel}
                  <div className="flex flex-col gap-4 w-full">
                    <div className="grid grid-cols-12">
                      <div className="col-span-12 md:col-span-4 lg:col-span-3">
                        <RadioButton
                          index={0}
                          text={"Prepaid by firm"}
                          selected={true}
                          handleRadioButtonChange={() => {}}
                          name="paymentMethod"
                        />
                      </div>
                      <div className="col-span-12 md:col-span-8 lg:col-span-9 text-md md:text-lg text-gray-font pl-8 md:pl-4 lg:pl-6">
                        Registration fees have been prepaid by firm.
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            {shouldHidePaymentMethodSelection &&
              isHostFirm &&
              sponsorPaymentMessage && (
                <>
                  <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
                    <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
                      Payment
                    </h3>
                  </div>
                  <div className="block text-left px-4 md:px-8 flex-1 w-full overflow-hidden pb-3 md:pb-6">
                    {totalEventFeeLabel}
                    <p className="text-sm md:text-base text-primary font-medium">
                      {sponsorPaymentMessage}
                    </p>
                  </div>
                </>
              )}
            {!shouldHidePaymentMethodSelection && (
              <>
                <div className="border-b border-primary pb-0 flex-shrink-0 mb-4">
                  <h3 className="text-[22px] font-normal text-gray-font leading-4 md:leading-5 mb-2 text-left font-sans pl-4 md:pl-8">
                    {isPrepaid && hasSponsorExtraCharges
                      ? "Additional Payment"
                      : "Payment"}
                  </h3>
                </div>
                <div className="block text-left px-4 md:px-8 flex-1 w-full overflow-hidden">
                  {totalEventFeeLabel}
                  {sponsorPaymentMessage && (
                    <p className="text-sm md:text-base text-primary font-medium mb-4">
                      {sponsorPaymentMessage}
                    </p>
                  )}
                  <div className="flex flex-col gap-4 w-full">
                    {paymentMethods.map((method, index) => (
                      <div key={method.id} className="grid grid-cols-12">
                        <div className="col-span-12 md:col-span-4 lg:col-span-3">
                          <RadioButton
                            index={index}
                            text={method.name}
                            selected={paymentMethod === method.id}
                            handleRadioButtonChange={() => {
                              setPaymentMethod(method.id as PaymentMethod);
                            }}
                            name="paymentMethod"
                          />
                        </div>
                        <div className="col-span-12 md:col-span-8 lg:col-span-9 text-md md:text-lg text-gray-font pl-8 md:pl-4 lg:pl-6">
                          {paymentDescription[
                            method.id as keyof typeof paymentDescription
                          ] ?? ""}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              </>
            )}
          </div>
        )}

        <div className="flex justify-start gap-4 mt-4">
          <LegacyButton
            text="GO BACK"
            content="textIcon"
            icon={
              <img
                src={chevronRightButton}
                alt="chevron left"
                className="w-3 h-3 rotate-180"
              />
            }
            iconOrder="iconFirst"
            color="neutral"
            className="!w-full md:!w-[205px]"
            onClick={() => navigate(-1)}
            type="button"
          />
          <LegacyButton
            text={isSubmitting ? "SUBMITTING..." : "SUBMIT"}
            content="textIcon"
            icon={
              <img src={chevronRightButton} alt="chevron" className="w-3 h-3" />
            }
            color="primary"
            className="!w-full md:!w-[205px]"
            onClick={handleSubmit}
            type="button"
            disabled={isSubmitting}
          />
        </div>
      </div>
    </div>
  );
};
